import { externalMapLink } from "../../../geo/service";
/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 12 de mayo de 2025
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS y no puede ser distribuido, copiado,
 * modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por
 * escrito de la empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table,
  Button,
  Modal,
  Dropdown,
  message,
  Spin,
  DatePicker,
  Row,
  Col,
  Tooltip,
  Input,
  Select,
  Space,
} from "antd";
import {
  MoreOutlined,
  CalendarOutlined,
  LeftOutlined,
  RightOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import locale from "antd/es/date-picker/locale/es_ES";
import dayjs from "dayjs";
import "dayjs/locale/es";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useUser } from "../../../UserContext";

const { RangePicker } = DatePicker;

const API_URL = process.env.REACT_APP_API_URL;

const History = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [modalVisible, setModalVisible] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pageSize, setPageSize] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [zonas, setZonas] = useState([]);
  const [selectedZona, setSelectedZona] = useState(null);
  const [loadingZonas, setLoadingZonas] = useState(false);
  const [vehiculos, setVehiculos] = useState([]);
  const [selectedVehiculo, setSelectedVehiculo] = useState(null);
  const [dateRange, setDateRange] = useState(null); // [fecha_inicio, fecha_fin]
  // Obtener usuario actual con permisos
  const { user: currentUser } = useUser();

  // Función de permisos
  const hasPermission = useCallback(
    (permission) => {
      const module = currentUser?.modulos?.find((m) => m.key === "Transporte");
      if (!module || module.estado !== "disponible") return false;
      return currentUser?.modulosPermisos?.some(
        (mp) => mp.modulo === "Transporte" && mp.permiso === permission
      );
    },
    [currentUser]
  );

  useEffect(() => {
    setSelectedDate(dayjs());
    fetchZonas();
    fetchVehiculos();
  }, []);

  const fetchZonas = async () => {
    setLoadingZonas(true);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await fetch(`${API_URL}/pre-operaciones/zonas-disponibles`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      if (!res.ok) throw new Error("Error al cargar zonas");
      const json = await res.json();
      setZonas(json.zonas || []);
    } catch (error) {
      console.error(error);
      message.error("Error al cargar zonas");
    } finally {
      setLoadingZonas(false);
    }
  };

  const fetchVehiculos = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const res = await fetch(`${API_URL}/vehiculos/codigo-placa`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      if (!res.ok) throw new Error("Error al cargar vehículos");
      const json = await res.json();
      setVehiculos(json);
    } catch (error) {
      console.error(error);
      message.error("Error al cargar vehículos");
    }
  };

  const fetchHistorial = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");

      // Construir parámetros de la URL
      let params = new URLSearchParams();

      // Si hay vehículo seleccionado y rango de fechas, usar rango
      if (selectedVehiculo && dateRange && dateRange[0] && dateRange[1]) {
        params.append("fecha_inicio", dateRange[0].format("YYYY-MM-DD"));
        params.append("fecha_fin", dateRange[1].format("YYYY-MM-DD"));
        params.append("vehiculo_codigo", selectedVehiculo);
      } else {
        // Comportamiento por defecto: fecha única
        params.append("fecha", selectedDate.format("YYYY-MM-DD"));
      }

      const res = await fetch(
        `${API_URL}/pre-operaciones/respuestas?${params.toString()}`,
        { headers: { Authorization: token ? `Bearer ${token}` : "" } }
      );

      if (!res.ok) throw new Error("Error al cargar historial");
      const json = await res.json();

      setData(json);
    } catch (error) {
      console.error(error);
      message.error("Error al cargar historial");
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedVehiculo, dateRange]);

  useEffect(() => {
    fetchHistorial();
  }, [fetchHistorial, selectedDate, dateRange]);

  const navigateDate = (days) => {
    setSelectedDate(selectedDate.add(days, "day"));
  };

  const handlePDFAction = async (action, record) => {
    const token = localStorage.getItem("accessToken");
    setPdfLoading(true);

    try {
      const res = await fetch(
        `${API_URL}/pre-operaciones/respuestas/pdf/${record.pdfFileName}`,
        { headers: { Authorization: token ? `Bearer ${token}` : "" } }
      );

      if (!res.ok) throw new Error("Error al obtener PDF");

      const blob = await res.blob();

      if (action === "view") {
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        setModalVisible(true);
      } else {
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = record.pdfFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }
    } catch (err) {
      console.error(err);
      message.error(
        `Error al ${action === "view" ? "ver" : "descargar"} el PDF`
      );
    } finally {
      setPdfLoading(false);
    }
  };

  // Función para abrir ubicación en Google Maps
  const openLocationInMaps = (ubicacion) => {
    if (!ubicacion || !ubicacion.latitud || !ubicacion.longitud) {
      message.warning("No hay datos de ubicación disponibles");
      return;
    }

    const mapsUrl = externalMapLink(ubicacion.latitud,ubicacion.longitud);
    window.open(mapsUrl, "_blank");
  };

  // Exportación ancha: cada pregunta como columna
  const exportToExcel = async () => {
    if (!data || data.length === 0) {
      message.warning("No hay registros para exportar");
      return;
    }

    try {
      // 1) Recopilar preguntas únicas
      const allQuestions = Array.from(
        new Set(data.flatMap((item) => item.respuestas.map((r) => r.pregunta)))
      );

      // 2) Armar filas con respuestas concatenadas
      const rows = data.map((item) => {
        const row = {
          Conductor: item.conductor?.nombre || "N/A",
          "Vehículo Código": item.vehiculo?.codigo || "N/A",
          "Vehículo Placa": item.vehiculo?.placa || "N/A",
          Fecha: dayjs(item.fecha).isValid()
            ? dayjs(item.fecha).format("DD/MM/YYYY")
            : "Fecha inválida",
          Hora: item.hora || "N/A",
        };
        item.respuestas.forEach((r) => {
          row[r.pregunta] = Array.isArray(r.respuesta)
            ? r.respuesta.join(", ")
            : r.respuesta;
        });
        return row;
      });

      // 3) Crear workbook y hoja dinámicamente
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("HistorialWide");
      sheet.columns = [
        { header: "Conductor", key: "Conductor", width: 25 },
        { header: "Vehículo Código", key: "Vehículo Código", width: 15 },
        { header: "Vehículo Placa", key: "Vehículo Placa", width: 15 },
        { header: "Fecha", key: "Fecha", width: 12 },
        { header: "Hora", key: "Hora", width: 10 },
        ...allQuestions.map((q) => ({ header: q, key: q, width: 30 })),
      ];
      sheet.addRows(rows);

      // 4) Forzar texto para prevenir fórmulas
      sheet.eachRow((_, rowNumber) => {
        sheet.getRow(rowNumber).eachCell((cell) => {
          if (typeof cell.value === "string") {
            cell.value = ` ${cell.value}`;
            cell.numFmt = "@";
          }
        });
      });

      // 5) Generar buffer y descargar
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `HistorialWide_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`);
      message.success("Exportación ancha completada");
    } catch (error) {
      console.error("Error en exportación ancha:", error);
      message.error("Error al generar el reporte");
    }
  };

  const columns = [
    {
      title: "Conductor",
      dataIndex: ["conductor", "nombre"],
      key: "conductor",
      align: "center",
      sorter: (a, b) =>
        (a.conductor?.nombre || "").localeCompare(b.conductor?.nombre || ""),
      sortDirections: ["ascend", "descend"],
      filteredValue: searchText ? [searchText] : null,
      onFilter: (value, record) => {
        const conductorName = record.conductor?.nombre || "";
        return conductorName.toLowerCase().includes(value.toLowerCase());
      },
    },
    {
      title: "Vehículo Código",
      dataIndex: ["vehiculo", "codigo"],
      key: "codigo",
      align: "center",
      sorter: (a, b) =>
        (a.vehiculo?.codigo || "").localeCompare(b.vehiculo?.codigo || ""),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Vehículo Placa",
      dataIndex: ["vehiculo", "placa"],
      key: "placa",
      align: "center",
      sorter: (a, b) =>
        (a.vehiculo?.placa || "").localeCompare(b.vehiculo?.placa || ""),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Zona",
      key: "zona",
      align: "center",
      render: (_, record) => {
        return record.vehiculo?.zona?.nombre || "Sin zona";
      },
      sorter: (a, b) => {
        const zonaA = a.vehiculo?.zona?.nombre || "";
        const zonaB = b.vehiculo?.zona?.nombre || "";
        return zonaA.localeCompare(zonaB);
      },
      sortDirections: ["ascend", "descend"],
      filteredValue: selectedZona ? [selectedZona] : null,
      onFilter: (value, record) => {
        return record.vehiculo?.zona?.nombre === value;
      },
    },
    {
      title: "Fecha",
      dataIndex: "fecha",
      key: "fecha",
      align: "center",
      render: (text) => dayjs(text).format("DD/MM/YYYY"),
      sorter: (a, b) => dayjs(a.fecha).unix() - dayjs(b.fecha).unix(),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Hora",
      dataIndex: "hora",
      key: "hora",
      align: "center",
      sorter: (a, b) => {
        const [ha, ma] = (a.hora || "00:00").split(":").map(Number);
        const [hb, mb] = (b.hora || "00:00").split(":").map(Number);
        return ha * 60 + ma - (hb * 60 + mb);
      },
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Acciones",
      key: "acciones",
      align: "center",
      width: 100,
      render: (_, record) => {
        const menuItems = [
          {
            key: "view",
            label: "Ver PDF",
            onClick: () => handlePDFAction("view", record),
            disabled: !hasPermission("VISUALIZAR_T"),
          },
          {
            key: "download",
            label: "Descargar PDF",
            onClick: () => handlePDFAction("download", record),
            disabled: !hasPermission("DESCARGAR_T"),
          },
        ];

        if (
          record.ubicacion &&
          record.ubicacion.latitud &&
          record.ubicacion.longitud
        ) {
          menuItems.push({
            key: "location",
            label: "Ver ubicación",
            icon: <EnvironmentOutlined />,
            onClick: () => openLocationInMaps(record.ubicacion),
          });
        }

        return (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
            <Button
              type="text"
              icon={<MoreOutlined />}
              aria-label="Acciones PDF"
              disabled={
                !hasPermission("VISUALIZAR_T") && !hasPermission("DESCARGAR_T")
              }
            />
          </Dropdown>
        );
      },
    },
  ];

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchesZona =
        !selectedZona || item.vehiculo?.zona?.nombre === selectedZona;
      const matchesVehiculo =
        !selectedVehiculo || item.vehiculo?.codigo === selectedVehiculo;
      return matchesZona && matchesVehiculo;
    });
  }, [data, selectedZona, selectedVehiculo]);

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Row gutter={16} align="middle">
            <Col>
              <Input.Search
                placeholder="Buscar por nombre..."
                allowClear
                onSearch={setSearchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 250, marginRight: 16 }}
              />
            </Col>
            <Col>
              <Select
                placeholder="Filtrar por Zona"
                allowClear
                style={{ width: 200, marginRight: 16 }}
                onChange={(value) => setSelectedZona(value)}
                loading={loadingZonas}
                value={selectedZona}
              >
                {zonas.map((zona) => (
                  <Select.Option key={zona.id} value={zona.nombre}>
                    {zona.nombre}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col>
              <Select
                placeholder="Filtrar por Vehículo"
                allowClear
                style={{ width: 200, marginRight: 16 }}
                onChange={(value) => {
                  setSelectedVehiculo(value);
                  // Limpiar rango de fechas si se deselecciona el vehículo
                  if (!value) {
                    setDateRange(null);
                  }
                }}
                value={selectedVehiculo}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {vehiculos.map((vehiculo) => (
                  <Select.Option key={vehiculo.codigo} value={vehiculo.codigo}>
                    {`${vehiculo.codigo} - ${vehiculo.placa}`}
                  </Select.Option>
                ))}
              </Select>
            </Col>

            {/* Mostrar RangePicker cuando hay vehículo seleccionado, de lo contrario navegación por fecha única */}
            {selectedVehiculo ? (
              <Col>
                <Space>
                  <RangePicker
                    value={dateRange}
                    onChange={(dates) => setDateRange(dates)}
                    format="DD/MM/YYYY"
                    locale={locale}
                    allowClear
                    placeholder={["Fecha inicio", "Fecha fin"]}
                    style={{ width: 280 }}
                  />
                  {dateRange && (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => setDateRange(null)}
                    >
                      Limpiar
                    </Button>
                  )}
                </Space>
              </Col>
            ) : (
              <>
                <Col>
                  <Tooltip title="Día anterior">
                    <Button
                      onClick={() => navigateDate(-1)}
                      icon={<LeftOutlined />}
                      shape="circle"
                    />
                  </Tooltip>
                </Col>

                <Col>
                  <DatePicker
                    value={selectedDate}
                    onChange={(date) => setSelectedDate(date || dayjs())}
                    format="DD/MM/YYYY"
                    locale={locale}
                    allowClear={false}
                    suffixIcon={<CalendarOutlined />}
                    style={{ width: 120 }}
                    inputReadOnly
                  />
                </Col>

                <Col>
                  <Tooltip title="Día siguiente">
                    <Button
                      onClick={() => navigateDate(1)}
                      icon={<RightOutlined />}
                      shape="circle"
                    />
                  </Tooltip>
                </Col>
              </>
            )}
          </Row>
        </Col>
        <Col>
          {hasPermission("DESCARGAR_T") && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={exportToExcel}
              style={{
                backgroundColor: "#52c41a",
                borderColor: "#52c41a",
              }}
            />
          )}
        </Col>
      </Row>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={filteredData}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: filteredData.length,
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "25", "50", "100"],
          onShowSizeChange: (_, size) => {
            setPageSize(size);
            setCurrentPage(1);
          },
          onChange: (page) => setCurrentPage(page),
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} PreOperacionales`,
        }}
        scroll={{ x: 800 }}
      />

      {modalVisible && hasPermission("VISUALIZAR_T") && (
        <Modal
          title={`Vista previa PDF - ${selectedDate.format("DD/MM/YYYY")}`}
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
          }}
          footer={null}
          width="90%"
          style={{ top: 20 }}
          destroyOnHidden
        >
          {pdfLoading ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <Spin size="large" />
              <p style={{ marginTop: 16 }}>Cargando documento...</p>
            </div>
          ) : (
            pdfUrl && (
              <iframe
                src={pdfUrl}
                style={{
                  width: "100%",
                  height: "75vh",
                  border: "none",
                  borderRadius: 8,
                }}
                title="Vista previa PDF"
              />
            )
          )}
        </Modal>
      )}
    </div>
  );
};

export default History;
